/**
 * The fixed menu of pass/fail predicates an admin-authored tutorial lesson can
 * pick from (src/admin's TutorialsTab), so a brand-new lesson is authored
 * entirely as DATA — no code change needed. Every built-in lesson in
 * missions.ts keeps its own hand-written predicate; this registry only backs
 * lessons created through the editor (see tutorial/runtime.ts).
 *
 * Deliberately a small, closed set mirroring the shapes the built-in
 * curriculum already uses (hit / soft / hard / spin / massé / bank / scored) —
 * see missions.ts's own predicates for the reasoning behind each threshold.
 */
import { cueCushions, hitObjectBall, SOFT_SPEED, SPIN_TIP, MASSE_TILT, POWER_CUSHIONS, type ShotContext } from './missions';

export type CheckTemplateId = 'always' | 'hitAny' | 'softHit' | 'hardHit' | 'scored' | 'bank' | 'tipThreshold' | 'masse';

/** Every field is optional; templates fall back to the same thresholds missions.ts uses. */
export interface CheckParams {
  maxSpeed?: number;
  minCushions?: number;
  /** tipThreshold only: which axis of tip offset ('a' = side/left-right, 'b' = top/bottom). */
  axis?: 'a' | 'b';
  /** tipThreshold only: 1 requires >= +minTip, -1 requires <= -minTip. */
  sign?: 1 | -1;
  minTip?: number;
  minTilt?: number;
}

export const CHECK_TEMPLATES: readonly { id: CheckTemplateId; params: (keyof CheckParams)[] }[] = [
  { id: 'always', params: [] },
  { id: 'hitAny', params: [] },
  { id: 'softHit', params: ['maxSpeed'] },
  { id: 'hardHit', params: ['minCushions'] },
  { id: 'scored', params: [] },
  { id: 'bank', params: ['minCushions'] },
  { id: 'tipThreshold', params: ['axis', 'sign', 'minTip'] },
  { id: 'masse', params: ['minTilt'] },
];

/** Evaluate one template against a finished shot — the whole of a custom lesson's `check`. */
export function evalCheckTemplate(type: CheckTemplateId, params: CheckParams, ctx: ShotContext): boolean {
  switch (type) {
    case 'always':
      return true;
    case 'hitAny':
      return hitObjectBall(ctx);
    case 'softHit':
      return hitObjectBall(ctx) && ctx.cue.speed <= (params.maxSpeed ?? SOFT_SPEED);
    case 'hardHit':
      return cueCushions(ctx) >= (params.minCushions ?? POWER_CUSHIONS);
    case 'scored':
      return ctx.evaluation.scored;
    case 'bank':
      return hitObjectBall(ctx) && ctx.evaluation.cushionsBeforeFirst >= (params.minCushions ?? 1);
    case 'tipThreshold': {
      const v = params.axis === 'b' ? ctx.cue.tipOffset.b : ctx.cue.tipOffset.a;
      const min = params.minTip ?? SPIN_TIP;
      return (params.sign ?? 1) >= 0 ? v >= min : v <= -min;
    }
    case 'masse':
      return ctx.cue.elevation >= (params.minTilt ?? MASSE_TILT);
    default:
      return false;
  }
}
